import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenWelcome } from '@gitbook/app-navigation';

import { useNavigation } from '../../navigation';
import { FLOW_STEPS, useFlowContext } from './useFlowContext';
import { default as WelcomeScreen } from './WelcomeScreen';

function assertOrgAndSiteCreated({
    organization,
    site,
}: {
    organization: api.Organization;
    site: api.Site;
}) {
    if (!organization) {
        throw new Error('Could not create organization');
    }

    if (!site) {
        throw new Error('Site must be created');
    }
}

/**
 * First screen as part of the user onboarding.
 * Also used when creating an organization.
 */
export default function OnboardingFlow(props: {
    screen: NavigationScreenWelcome;
}): React.ReactElement | null {
    const { screen } = props;
    const { navigate } = useNavigation();
    const [state, setStep] = useFlowContext();

    switch (state.step) {
        case FLOW_STEPS.CREATE_ORGANIZATION:
            return (
                <WelcomeScreen
                    screen={screen}
                    onNext={({ organization, site }) => {
                        setStep({
                            step: FLOW_STEPS.COMPLETE,
                            context: { organization, site },
                        });
                    }}
                />
            );

        case FLOW_STEPS.COMPLETE:
            assertOrgAndSiteCreated(state.context);

            navigate({
                screen: {
                    type: 'site',
                    organization: state.context.organization.id,
                    site: state.context.site.id,
                },
            });

            return null;

        default:
            return null;
    }
}
